import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';
import { companyInfo } from '../../data/mock';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [aboutDropdown, setAboutDropdown] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', path: '/' },
    { 
      name: 'Acerca de', 
      path: '/acerca-de',
      submenu: [
        { name: 'Historia', path: '/acerca-de/historia' },
        { name: 'Comisiones', path: '/acerca-de/comisiones' },
        { name: 'Directorio', path: '/acerca-de/directorio' },
      ]
    },
    { name: 'Noticias', path: '/noticias' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contacto', path: '/contacto' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0A1628] text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Phone className="h-3.5 w-3.5" />
                {companyInfo.phone}
              </a>
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-blue-400 transition-colors">
                <Mail className="h-3.5 w-3.5" />
                {companyInfo.email}
              </a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5" />
              <span>{companyInfo.address}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_business-hub-mx/artifacts/ici1bquu_LogoHorizontal.png" 
                alt="COPARMEX Manzanillo" 
                className="h-12 sm:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div key={link.name} className="relative group">
                  {link.submenu ? (
                    <div
                      className="relative"
                      onMouseEnter={() => setAboutDropdown(true)}
                      onMouseLeave={() => setAboutDropdown(false)}
                    >
                      <button
                        className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1 transition-all duration-200 ${
                          isActive(link.path)
                            ? 'text-[#1E40AF] bg-blue-50'
                            : 'text-gray-700 hover:text-[#1E40AF] hover:bg-blue-50'
                        }`}
                      >
                        {link.name}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${aboutDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {/* Dropdown */}
                      <div className={`absolute top-full left-0 mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden transition-all duration-200 ${
                        aboutDropdown ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                      }`}>
                        {link.submenu.map((sublink) => (
                          <Link
                            key={sublink.name}
                            to={sublink.path}
                            className={`block px-4 py-3 text-sm transition-colors ${
                              location.pathname === sublink.path
                                ? 'bg-blue-50 text-[#1E40AF] font-medium'
                                : 'text-gray-700 hover:bg-blue-50 hover:text-[#1E40AF]'
                            }`}
                          >
                            {sublink.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive(link.path)
                          ? 'text-[#1E40AF] bg-blue-50'
                          : 'text-gray-700 hover:text-[#1E40AF] hover:bg-blue-50'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link
                to="/contacto"
                className="inline-flex items-center px-5 py-2.5 bg-[#1E40AF] text-white text-sm font-medium rounded-lg hover:bg-[#1E3A8A] transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Afíliate
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isOpen ? 'max-h-screen' : 'max-h-0'
        }`}>
          <div className="px-4 py-4 bg-gray-50 border-t">
            {navLinks.map((link) => (
              <div key={link.name}>
                {link.submenu ? (
                  <>
                    <div className="px-4 py-3 text-sm font-medium text-gray-700">
                      {link.name}
                    </div>
                    <div className="pl-4">
                      {link.submenu.map((sublink) => (
                        <Link
                          key={sublink.name}
                          to={sublink.path}
                          className="block px-4 py-2 text-sm text-gray-600 hover:text-[#1E40AF]"
                          onClick={() => setIsOpen(false)}
                        >
                          {sublink.name}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={link.path}
                    className={`block px-4 py-3 text-sm font-medium rounded-lg ${
                      isActive(link.path)
                        ? 'text-[#1E40AF] bg-blue-50'
                        : 'text-gray-700'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                )}
              </div>
            ))}
            <Link
              to="/contacto"
              className="block mt-4 px-4 py-3 bg-[#1E40AF] text-white text-sm font-medium rounded-lg text-center"
              onClick={() => setIsOpen(false)}
            >
              Afíliate
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
